import { useState } from 'react';
import { Users, Check } from 'lucide-react';
import { apiPost, errorMessage, isApiError } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { formatDate } from '../../lib/dates';
import type { PublicPoll } from '../../types';
import type { SectionProps } from './useSectionQuery';
import { SectionShell } from './SectionShell';

/**
 * Only there while a poll is open for votes: no "no open polls" card taking up
 * space the rest of the time. The Worker already drops a poll past its end
 * date; `is_open` is checked too, in case one closes while the page is open.
 *
 * It stays up while the dashboard reloads after a vote, so the bars fill in
 * rather than the whole box blinking out and back.
 */
export function PollWidget(props: SectionProps<PublicPoll>) {
    const { data: poll, onRetry } = props;
    const toast = useToast();
    const [voting, setVoting] = useState(false);

    if (!poll || !poll.is_open) return null;

    const vote = async (optionId: string) => {
        if (voting || poll.my_vote) return;
        setVoting(true);
        try {
            await apiPost(`/api/polls/${poll.id}/vote`, { option_id: optionId });
            toast.success('Thanks for voting!');
        } catch (err) {
            if (isApiError(err) && err.status === 409) toast.info("You've already voted on this poll.");
            else toast.error(errorMessage(err, "Couldn't save your vote."));
        } finally {
            setVoting(false);
            // Reload the dashboard so the bars show the new count.
            onRetry();
        }
    };

    const showResults = !!poll.my_vote;
    const voters = `${poll.total_votes} ${poll.total_votes === 1 ? 'resident has' : 'residents have'} voted`;

    return (
        <SectionShell title="Community voice" icon={<Users size={20} aria-hidden="true" />} linkTo="/polls" linkLabel="All polls">
            <div className="card poll-card">
                <h3>{poll.question}</h3>
                <div className="poll-options" role={showResults ? undefined : 'group'} aria-label="Poll options">
                    {poll.options.map((option) => {
                        const percentage = poll.total_votes > 0 ? Math.round((option.votes / poll.total_votes) * 100) : 0;
                        const isVoted = poll.my_vote === option.id;
                        return (
                            <button
                                key={option.id}
                                type="button"
                                className={`poll-option ${isVoted ? 'voted' : ''} ${showResults ? 'disabled' : ''}`}
                                onClick={() => { void vote(option.id); }}
                                disabled={showResults || voting}
                                aria-pressed={isVoted}
                            >
                                <div className="poll-option-bar" style={{ width: showResults ? `${percentage}%` : '0%' }} aria-hidden="true" />
                                <span className="poll-option-text">
                                    {isVoted && <Check size={16} aria-hidden="true" />}
                                    {option.text}
                                </span>
                                {showResults && <span className="poll-option-percent">{percentage}%</span>}
                            </button>
                        );
                    })}
                </div>
                <p className="poll-meta">
                    {showResults ? `You voted · ${voters}` : `${voters} · Tap to vote`}
                    {poll.ends_at && <> · Closes {formatDate(poll.ends_at)}</>}
                </p>
            </div>
        </SectionShell>
    );
}
